/**
 * One turn's activity feed: live progress, then a summary that auto-collapses
 * into a one-line chip. Sits where `ThinkingLine` used to (§2.2, §5.3) —
 * `Thread.tsx` decides whether to draw this or the legacy line.
 */

import { useActivity, type Feed, type Step } from './activityStore';
import ActivityRow, { formatDuration } from './ActivityRow';
import { cn } from '../lib/cn';
import type { ActivitySummary } from './types';

function groupSteps(steps: Step[]): Step[][] {
  const groups: Step[][] = [];
  let current = -1;
  for (const step of steps) {
    if (step.group !== current) {
      groups.push([]);
      current = step.group;
    }
    groups[groups.length - 1]!.push(step);
  }
  return groups;
}

function pluralSteps(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'шаг';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'шага';
  return 'шагов';
}

/**
 * `summary.ms` is the WHOLE turn by wall clock, not a sum of the steps' own
 * durations (§2.10/§3.3) — on a parallel batch it is smaller than that sum,
 * and that is correct: it is how long the visitor actually waited.
 */
function summaryText(summary: ActivitySummary | null, steps: Step[]): string {
  const count = summary?.steps ?? steps.length;
  const ms = summary?.ms ?? 0;
  const failed = summary?.failed ?? 0;
  const parts = [`${count} ${pluralSteps(count)}`];
  if (ms > 0) parts.push(formatDuration(ms));
  let text = parts.join(' · ');
  if (failed > 0) text += ' · есть без ответа';
  return text;
}

function liveHeaderText(feed: Feed): string {
  const running = feed.steps.filter((s) => s.phase === 'running').length;
  if (running === 0) return 'Думаю…';
  return `${feed.steps.length} ${pluralSteps(feed.steps.length)}`;
}

export default function ActivityFeed({ turn }: { turn: number }) {
  const feed = useActivity((state) => state.feeds.find((f) => f.turn === turn));
  const toggle = useActivity((state) => state.toggle);

  if (!feed) return null;
  // §5.2: a turn with no steps draws nothing once it is over — no "0 шагов"
  // line under an answer that used no tools.
  if (feed.finished && feed.steps.length === 0) return null;

  // One persistent tree for both the collapsed chip and the expanded list —
  // so the transition between them is the BODY's height (§5.5.3), not a
  // React remount that would jump instantly with nothing to animate.
  const collapsed = feed.finished && feed.collapsed;
  const groups = groupSteps(feed.steps);

  const header = collapsed ? (
    <>
      <span className="activity-feed__caret" aria-hidden="true">
        ▸
      </span>
      <span className="activity-feed__summary">{summaryText(feed.summary, feed.steps)}</span>
    </>
  ) : feed.finished ? (
    summaryText(feed.summary, feed.steps)
  ) : (
    <>
      <span className="activity-feed__dot" aria-hidden="true" />
      {liveHeaderText(feed)}
    </>
  );

  return (
    <div className={cn('activity-feed', collapsed && 'activity-feed--collapsed')}>
      {feed.finished ? (
        // No `role="status"`/`aria-live` here (found on review of the sibling
        // `ReasoningBlock`, !265 — a miss from !259): an explicit role
        // REPLACES the implicit `button` role, so assistive tech reads a
        // status region rather than a widget, and `aria-expanded` ends up on
        // a non-widget role. The live region has no job here either: once
        // the turn is finished nothing in this header updates again.
        <button
          type="button"
          className="activity-feed__header activity-feed__header--button"
          aria-expanded={!collapsed}
          onClick={() => toggle(turn)}
        >
          {header}
        </button>
      ) : (
        <div className="activity-feed__header" role="status" aria-live="polite">
          {header}
        </div>
      )}
      <div className="activity-feed__body">
        <div className="activity-feed__body-inner">
          {groups.map((group, i) => (
            <ul key={i} className={cn('activity-feed__group', group.length > 1 && 'activity-feed__group--bracket')}>
              {group.map((step) => (
                <ActivityRow key={step.id} step={step} />
              ))}
            </ul>
          ))}
        </div>
      </div>
    </div>
  );
}
